import { createSlice } from '@reduxjs/toolkit';

// Stundensätze und Materialaufschlag stehen nicht hier, sondern in den
// Lohngruppen und im Zuschlagsprofil (state.lohnsaetze, utils/kalkulation).
export const KALKULATION_DEFAULTS = {
  taxRate: 19,
  kmRate: 0.90,
};

const initialState = {
  aiOptimizationEnabled: false,
  customAiPrompt: '',
  companyName: '',
  currency: 'EUR',
  firstDayOfWeek: 'Monday',
  workingDaysPerWeek: 5,
  preferredTimeStart: '08:00',
  preferredTimeEnd: '18:00',
  dateFormat: 'MM/DD/YYYY',
  hourFormat: '24 Hour clock',
  timeSpanFormat: '[h]:mm:ss',
  roundingMethod: 'none',
  roundingInterval: '5',
  remindOnSchedule: false,
  remindOnEstimate: false,
  kalkulation: { ...KALKULATION_DEFAULTS },
  company: {
    name: '', inhaber: '', street: '', zip: '', city: '',
    phone: '', fax: '', email: '', website: '',
    steuernummer: '', ustid: '',
    iban: '', bic: '', bank: '',
    logo: '',
  },
  // 'demo' = Präsentationsmodus mit Seed-Daten, 'live' = echter Betrieb mit Onboarding.
  workspaceMode: 'demo',
  onboarding: { completed: false, step: 0, skipped: false },
  briefpapier: {},
  // Verrechnungssätze der Lohngruppen zum heutigen Tag: { lohngruppen: [{id, name, vs, lks, ist_standard}], standard }
  lohnsaetze: { lohngruppen: [], standard: null },
};

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setAiOptimizationEnabled: (state, action) => {
      state.aiOptimizationEnabled = action.payload;
    },
    setCustomAiPrompt: (state, action) => {
      state.customAiPrompt = action.payload;
    },
    setCompanyName: (state, action) => {
      state.companyName = action.payload;
    },
    setCurrency: (state, action) => {
      state.currency = action.payload;
    },
    setFirstDayOfWeek: (state, action) => {
      state.firstDayOfWeek = action.payload;
    },
    setWorkingDaysPerWeek: (state, action) => {
      state.workingDaysPerWeek = action.payload;
    },
    setPreferredTimeStart: (state, action) => {
      state.preferredTimeStart = action.payload;
    },
    setPreferredTimeEnd: (state, action) => {
      state.preferredTimeEnd = action.payload;
    },
    setDateFormat: (state, action) => {
      state.dateFormat = action.payload;
    },
    setHourFormat: (state, action) => {
      state.hourFormat = action.payload;
    },
    setTimeSpanFormat: (state, action) => {
      state.timeSpanFormat = action.payload;
    },
    setRoundingMethod: (state, action) => {
      state.roundingMethod = action.payload;
    },
    setRoundingInterval: (state, action) => {
      state.roundingInterval = action.payload;
    },
    setRemindOnSchedule: (state, action) => {
      state.remindOnSchedule = action.payload;
    },
    setRemindOnEstimate: (state, action) => {
      state.remindOnEstimate = action.payload;
    },
    setWorkspaceMode: (state, action) => {
      state.workspaceMode = action.payload;
    },
    setOnboarding: (state, action) => {
      state.onboarding = { ...state.onboarding, ...action.payload };
    },
    setBriefpapier: (state, action) => {
      state.briefpapier = action.payload;
    },
    setAllSettings: (state, action) => {
      return { ...state, ...action.payload };
    },
    // Lohngruppen sind die einzige Quelle für Stundensätze.
    setLohnsaetze: (state, action) => {
      state.lohnsaetze = action.payload;
    }
  }
});

export const {
  setAiOptimizationEnabled,
  setCustomAiPrompt,
  setCompanyName,
  setCurrency,
  setFirstDayOfWeek,
  setWorkingDaysPerWeek,
  setPreferredTimeStart,
  setPreferredTimeEnd,
  setDateFormat,
  setHourFormat,
  setTimeSpanFormat,
  setRoundingMethod,
  setRoundingInterval,
  setRemindOnSchedule,
  setRemindOnEstimate,
  setWorkspaceMode,
  setOnboarding,
  setBriefpapier,
  setAllSettings,
  setLohnsaetze
} = settingsSlice.actions;

/** Verrechnungssatz für eine Rolle/Lohngruppe (Name), sonst der Standard. */
export function verrechnungssatzFuer(lohnsaetze, rolleOderId) {
  const liste = lohnsaetze?.lohngruppen || [];
  const schluessel = String(rolleOderId || '').toLowerCase().split(' ')[0];
  const treffer = liste.find((g) => g.id === rolleOderId) || liste.find((g) => g.name.toLowerCase() === schluessel);
  return (treffer || lohnsaetze?.standard)?.vs ?? 0;
}

/** Lohnkostensatz (EK) — für Ist-Kosten in der Nachkalkulation. */
export function lohnkostensatzFuer(lohnsaetze, rolleOderId) {
  const liste = lohnsaetze?.lohngruppen || [];
  const schluessel = String(rolleOderId || '').toLowerCase().split(' ')[0];
  const treffer = liste.find((g) => g.id === rolleOderId) || liste.find((g) => g.name.toLowerCase() === schluessel);
  return (treffer || lohnsaetze?.standard)?.lks ?? 0;
}

export default settingsSlice.reducer;
